import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import classes from './DataTable.module.css';
import { useMemo } from 'react';
import { DataTableHead } from './DataTableHead';
import { Order, HeadCell } from './interfaces';
import { DataTableToolbar } from './DataTableToolbar';
import { getComparator } from './getComparator.fn';
import { ActionsMenu } from './ActionsMenu';


export type Action<TData> = {
  onAction: (item: TData) => void;
  label: string;
}


type Props<TData, Key extends keyof TData> = {
  selectable?: boolean;
  selected?: TData[];
  setSelected?: (val: TData[]) => void;
  actions?: Action<TData>[];
  data: TData[];
  headCells: readonly HeadCell<TData, Key>[];
  initialOrderBy: Key;
  keyField: (row: TData) => React.Key;
  onRowClick?: (item: TData) => void;
  title?: string;
  minWidth?: number;
}


export function DataTable<TData, Key extends keyof TData>({ data, selected = [], setSelected, initialOrderBy, onRowClick, headCells, keyField, title, selectable, actions, minWidth = 750 }: Props<TData, Key>) {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<Key>(initialOrderBy);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: Key,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected?.(data);
      return;
    }
    setSelected?.([]);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, item: TData) => {
    const itemKey = keyField(item);
    if (onRowClick) {
      const row = data.find(row => keyField(row) === itemKey);
      if (row) onRowClick(row);
    }
    if (!selectable) return;

    const selectedIndex = selected?.findIndex(cur => itemKey === keyField(cur));

    const newSelected =
        selectedIndex === -1
      ? [...selected, item]
        : selectedIndex === 0
      ? selected.slice(1)
        : selectedIndex === selected.length - 1
      ? selected.slice(0, -1)
        : [...selected.slice(0, selectedIndex), ...selected.slice(selectedIndex + 1)];

    setSelected?.(newSelected);
  };

  function handleAction(action: Action<TData>, row: TData) {
    action.onAction(row);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (item: TData) => selected.findIndex(cur => keyField(item) === keyField(cur)) !== -1;

  // Avoid layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const comparator: (item: TData) => string | number | Date = useMemo(() =>
    headCells.find(d => d.id === orderBy)?.sortValue ?? (row => row[orderBy] as unknown as number | string) // cast to unknown to not overburden ts types checking, it is up to developer to provide correct sortable columns
  , [headCells, orderBy]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>

        { false && selectable && <DataTableToolbar numSelected={selected.length} title={title}/> }

        <TableContainer>
          <Table
            sx={{ minWidth }}
            aria-labelledby='tableTitle'
          >
            <DataTableHead
              selectable={selectable}
              hasActions={!!actions}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              headCells={headCells}
            />
            <TableBody>
              {data.slice().sort(getComparator(order, comparator))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      className={classes.row}
                      onClick={event => handleClick(event, row)}
                      role='checkbox'
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={keyField(row)}
                      selected={isItemSelected}
                    >
                      { selectable &&
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        </TableCell>
                      }

                      {headCells.map((col, i) =>
                        <TableCell
                          component={!i ? 'th' : undefined}
                          padding={ !i && selectable ? 'none' : 'normal' }
                          align={col.numeric ? 'right' : 'left'} key={col.id}
                        >{row[col.id]}</TableCell>
                      )}

                      { actions &&
                        <TableCell padding='checkbox'>
                          <ActionsMenu actions={actions} onAction={action => handleAction(action, row)}/>
                        </TableCell>
                      }
                    </TableRow>
                  );
                })}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={headCells.length + 1}/>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
